import { isNil, get } from 'lodash';

/**
 * @typedef {object} Feature
 * @typedef {object} Point
 */

import { SYMBOLOGY_TYPES, GEOJSON_GEOMETRIES, STYLE_TYPES } from './Styles';

/**
 * Gets the style id.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The id for the style or empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getId = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, '_id', '');
};

/**
 * Get style name.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The name of the style or empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getName = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'name', '');
};

/**
 * Gets the type of the style.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The type or empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getType = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'styleType', '');
};

/**
 * Checks if the style is a Raster style.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the style is Raster, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isRaster = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'styleType', '') === STYLE_TYPES.RASTER;
};

/**
 * Checks if the style is a GeoJSON style.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the style is GeoJSON, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isGeoJSON = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'styleType', '') === STYLE_TYPES.GEOJSON;
};

/**
 * Checks if the style is a MapBox style.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the style is MapBox, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isMapBox = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'styleType', '') === STYLE_TYPES.MAPBOX;
};

/**
 * Checks if the style is a custom style.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the style is custom, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isCustom = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'styleType', '') === STYLE_TYPES.CUSTOM;
};

/**
 * Get the style that leaflet will use for the geojson.
 *
 * @param {object} styleObject - The style object.
 * @returns {object} The object that stores the style for each geojson property or emtpy object is not found.
 * @throws {Error} If style is null or undefined.
 */
export const getGeoJSONStyle = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle', {});
};

/**
 * Get the geometry type of the geojson style.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The geometry type of the geojson style. Empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getGeoJSONGeometry = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.geometry', '');
};

/**
 * Get the symbology type for the style style.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The simbology type for the style. Empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getSymbologyType = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.type', '');
};

/**
 * Checks if the style's symbology type is single.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the simbology type is single, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isSingleSimbology = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.type', false) === SYMBOLOGY_TYPES.SINGLE;
};

/**
 * Checks if the style's symbology type is categorized.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the simbology type is categorized, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isCategorizedSimbology = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.type', false) === SYMBOLOGY_TYPES.CATEGORIZED;
};

/**
 * Checks if the style's symbology type is categorized.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the simbology type is numeric, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isNumericSimbology = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.type', false) === SYMBOLOGY_TYPES.NUMERIC;
};

/**
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the geometry type is LineString, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isGeometryLine = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.geometry', false) === GEOJSON_GEOMETRIES.LINESTRING;
};

/**
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the geometry type is Point, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isGeometryPoint = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.geometry', false) === GEOJSON_GEOMETRIES.POINT;
};

/**
 * Gets the color for geometries in geojson styles in hex format.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The color for the geometry in geojson styles. Hex format. Empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getColor = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.color', '');
};

/**
 * Gets the name of the property to use in categorized symbology.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The name of the property to use in categorized symbology. Empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getStyledProp = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.propName', '');
};

/**
 * Gets the legend for the style style.
 *
 * @param {object} styleObject - The style object.
 * @returns {object[]} The legend for the style styles. Empty array if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getLegend = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.legend', []);
};

/**
 * Gets the data limits for a numeric styled style.
 *
 * @param {object} styleObject - The style object.
 * @returns {number[]} Gets the data limits for a numeric styled style. Empty array if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getDataLimits = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.dataLimits', []);
};

/**
 * Gets the color range name for geometries in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The color range name for the geometry in geojson styles. Empty string if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getColorRange = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.colorRange', '');
};

/**
 * Gets the transparency for geometry in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {number} The transparency for the style or 0 if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getAlpha = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.alpha', 0);
};

/**
 * Gets the weight of the line for geometry in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {number} The weight for the style line or 1 if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getWeight = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.weight', 1);
};

/**
 * Gets the class of the Point in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The class for the icon or empty if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getIconClass = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.iconClass', '');
};

/**
 * Checks if the style is deleted.
 *
 * @param {object} styleObject - The style object.
 * @returns {boolean} True if the style is in trash, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isDeleted = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'deleted', '') !== 'no';
};

/**
 * Get the number of intervals to divide the data domain.
 *
 * @param {object} styleObject - The style object.
 * @returns {number|string} The number of intervals to divide the data, gives 1 if not defined.
 * @throws {Error} If style is null or undefined.
 */
export const getNumIntervals = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.numIntervals', 1);
};

/**
 * Get the tolerance of the GeoJSON VT layer.
 *
 * @param {object} styleObject - The style object.
 * @returns {number|string} The tolerance of the GeoJSON VT layer, gives 1 if not defined.
 * @throws {Error} If style is null or undefined.
 */
export const getTolerance = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.tolerance', 1);
};

/**
 * Get the opacity value in percentage for the raster style.
 *
 * @param {object} styleObject - The style object.
 * @returns {number} The opacity value in percentage of the raster style, gives 100 if not defined.
 * @throws {Error} If style is null or undefined.
 */
export const getRasterOpacityPercentage = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'rasterStyle.opacity', 100);
};

/**
 * Gets the color of the outline in geojson styles in hex format.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The color of the outline in geojson styles. Hex format. White if not found.
 * @throws {Error} If style is null or undefined.
 */
export const getBorderColor = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.borderColor', '#fff');
};

/**
 * Gets the transparency of the outline in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {number} The transparency of the outline or 0.8 if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getBorderAlpha = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.borderAlpha', 0.8);
};

/**
 * Gets the dashed style of the outline in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The dashed style of the outline or empty string if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getBorderStyle = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.borderStyle', '');
};

/**
 * Gets the type of point to draw in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The type of point to draw or empty string if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getPointType = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.pointType', '');
};

/**
 * Get the size of the point in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {number} The size of the point or 5 if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getPointSize = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.pointSize', 5);
};

/**
 * Gets the chosen category of the svg to draw in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The chosen category of the svg to draw or empty string if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getSVGCategory = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.svgCategoryId', '');
};

/**
 * Gets the url of the svg to draw in geojson styles.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The url of the svg to draw or empty string if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getSVGUrl = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'geojsonStyle.styleData.svgUrl', '');
};

/**
 * Gets the url of the custom style.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The url of the custom style or empty string if it does not exist.
 * @throws {Error} If style is null or undefined.
 */
export const getStyleURL = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'styleURL', '');
};

/**
 * Checks if the symbology is scaled or not.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} True if the symbology is scaled, false otherwise.
 * @throws {Error} If style is null or undefined.
 */
export const isScaledSymbology = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return (
    get(styleObject, 'geojsonStyle.styleData.isScaledSymbology', false) ||
    get(styleObject, 'isScaledCustom', false)
  );
};

/**
 * Get the SLD url for the raster style.
 *
 * @param {object} styleObject - The style object.
 * @returns {string} The SLD url, or empty string not defined.
 * @throws {Error} If style is null or undefined.
 */
export const getSLD = styleObject => {
  if (isNil(styleObject)) {
    throw new Error('style not defined');
  }
  return get(styleObject, 'rasterStyle.sld', '');
};
